import { useContext, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pushpress/pushpress-ui";

import SectionHeader from "../../shared/SectionHeader";
import TablePagination from "../../shared/TablePagination";
import { AppContext } from "./../../../../../context/app";
import { PAGINATION_PAGE_SIZE } from "../../../constants";
import ErrorState from "../../shared/ErrorState";
import TableSkeleton from "../../shared/TableSkeleton";
import { useGetClientUserCommunications } from "../../../../../apis/communicationsApi.hooks";
import { useGetClientUserMessageLogs } from "../../../../../apis/messagesApi.hooks";

import { columns } from "./columns";
import EmptyTable from "../../shared/EmptyTable";
type CommunicationsProps = {
  userUuid: string;
};

type TableRow = {
  timestamp: number;
  messageType: string;
  subject: string;
  status: string;
};

export default function Communications({ userUuid }: CommunicationsProps) {
  const {
    client: { uuid },
  } = useContext(AppContext);
  const {
    data: communicationData,
    isLoading,
    isError,
    refetch,
  } = useGetClientUserCommunications(uuid, userUuid);
  const {
    data: messageLogData,
    isLoading: messageLogLoading,
    isError: messageLogError,
    refetch: messageLogRefetch,
  } = useGetClientUserMessageLogs(uuid, userUuid);

  const [sorting] = useState<{ id: string; desc: boolean }[]>([
    { id: "timestamp", desc: true },
  ]);

  const convertToTableData = (
    communicationData,
    messageLogData
  ): TableRow[] => {
    let communications = [];
    let messageLogs = [];
    if (messageLogData?.messageLogs?.length) {
      messageLogs = messageLogData.messageLogs.map((messageLog) => {
        let status = "created";
        if (messageLog.opened) {
          status = "opened";
        } else if (messageLog.sent || messageLog.success) {
          status = "sent";
        }
        return {
          timestamp: messageLog.sendTimestamp,
          messageType: messageLog.messageType,
          subject: messageLog.messageSubject,
          status,
        };
      });
    }
    if (communicationData?.length) {
      communications = communicationData.map((communication) => {
        let status = "created";
        if (communication.openedAt) {
          status = "opened";
        } else if (communication.sentAt) {
          status = "sent";
        }
        return {
          timestamp: communication.sentAt
            ? communication.sentAt
            : communication.createdAt,
          messageType: communication.messageType,
          subject: communication.subject,
          status,
        };
      });
    }
    return communications.concat(messageLogs);
  };

  const tableData = useMemo(
    () => convertToTableData(communicationData, messageLogData),
    [communicationData, messageLogData]
  );
  const table = useReactTable({
    data: tableData,
    columns,
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const dataRefetch = () => {
    refetch();
    messageLogRefetch();
  };
  const loading = isLoading || messageLogLoading;
  const error = isError || messageLogError;

  const showPagination = !isLoading && tableData.length > PAGINATION_PAGE_SIZE;
  return (
    <div className="tw-space-y-4">
      <SectionHeader title="Communications" />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
        <div className="tw-rounded tw-ring-1 tw-ring-black/5 tw-mt-2">
          {error ? (
            <ErrorState refetch={dataRefetch} />
          ) : loading ? (
            <TableSkeleton
              numCols={columns.length}
              numRows={PAGINATION_PAGE_SIZE}
            />
          ) : (
            <Table className="tw-divide-y tw-divide-neutral-300 ">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} className="tw-bg-neutral-50">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="tw-divide-y tw-divide-neutral-200 tw-bg-white">
                {table.getRowModel()?.rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="tw-py-0">
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell key={cell.id} className={"tw-py-0"}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <EmptyTable
                    colLength={columns.length}
                    title="Any email or SMS messages sent to this user will appear here"
                  />
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {showPagination && <TablePagination table={table} />}
      </div>
    </div>
  );
}
